/**
 * Converts a string to Title Case following Canopy's style guide.
 *
 * Capitalizes the first letter of each word except articles (a, an, the),
 * prepositions, and conjunctions. First and last words are always capitalized.
 * Preserves certain acronyms (like "AI") in uppercase (uppercaseAcronyms set).
 *
 * @example
 * toTitleCase("the quick brown fox") // "The Quick Brown Fox"
 * toTitleCase("a tale of two cities") // "A Tale of Two Cities"
 * toTitleCase("canopy ai") // "Canopy AI"
 */

// Words that should remain lowercase unless they're the first or last word
const lowercaseWords = new Set([
  // Articles
  "a",
  "an",
  "the",
  // Common prepositions (following AP/Chicago style case conventions - typically 4 letters or fewer)
  "at",
  "by",
  "for",
  "from",
  "in",
  "into",
  "of",
  "off",
  "on",
  "onto",
  "out",
  "over",
  "to",
  "up",
  "with",
  "as",
  "via",
  // Conjunctions
  "and",
  "but",
  "or",
  "nor",
  "so",
  "yet",
]);

// Acronyms that should remain uppercase
const uppercaseAcronyms = new Set(["ai"]);

export function toTitleCase(str: string): string {
  if (!str || typeof str !== "string") {
    return "";
  }

  const words = str.trim().split(/\s+/);

  if (words.length === 0) {
    return "";
  }

  return words
    .map((word, index) => {
      if (!word) return word;

      const isFirstWord = index === 0;
      const isLastWord = index === words.length - 1;
      const wordLower = word.toLowerCase();

      // if wordLower has an exact match in the uppercaseAcronyms set, return the word in uppercase
      if (uppercaseAcronyms.has(wordLower)) {
        return wordLower.toUpperCase();
      }

      // Always capitalize first and last words
      if (isFirstWord || isLastWord) {
        return capitalizeWord(word);
      }

      // Keep articles, prepositions, and conjunctions lowercase in the middle
      if (lowercaseWords.has(wordLower)) {
        return wordLower;
      }

      // Capitalize all other words
      return capitalizeWord(word);
    })
    .join(" ");
}

function capitalizeWord(word: string): string {
  if (!word) return word;

  // Handle hyphenated words by capitalizing each part
  if (word.includes("-")) {
    return word
      .split("-")
      .map((part) => capitalizeWord(part))
      .join("-");
  }

  const firstLetterIndex = word.search(/[a-zA-Z]/);
  if (firstLetterIndex === -1) return word;

  const firstLetter = word[firstLetterIndex].toUpperCase();
  const beforeFirstLetter = word.substring(0, firstLetterIndex);
  const afterFirstLetter = word.substring(firstLetterIndex + 1).toLowerCase();

  return beforeFirstLetter + firstLetter + afterFirstLetter;
}
